<template>
    <div class="mobile-player-mat" :class="{ [position]: position }">
        <div class="mat-content">
            <div class="mat-options mat-options-left">
                <div class="units-group units-in-supply" v-if="expandedGroup === typeSupply">
                    <unit-stack
                        v-for="(unitStack, index) of groupUnitsByType(unitsInSupply(player))"
                        :key="`supply-unit-stack-${index}`"
                        :units="unitStack"
                    />
                    <bishop-toggle
                        :show="position === 'bottom' && selectedBishopInHand"
                        :onClickCallback="toggleRecruitType"
                        :enabled="bishopRecruit"
                    />
                </div>
                <div class="units-group eliminated-units" v-if="expandedGroup === typeEliminated">
                    <unit-stack
                        v-for="(unitStack, index) of groupUnitsByType(eliminatedUnits(player))"
                        :key="`eliminated-unit-stack-${index}`"
                        :units="unitStack"
                    />
                </div>
                <div class="mat-options-toolbar">
                    <switch-toggle
                        :icon="expandedGroup === typeEliminated ? 'skull' : 'coins'"
                        :checked="expandedGroup === typeEliminated"
                        :callback="onToggleSupplyEliminated"
                    />
                    <div class="view-cards" @click="onViewCards">
                        <icon icon="cards" class="view-cards-icon" />
                    </div>
                </div>
            </div>
            <div class="mat-options mat-options-center">
                <div class="units-group units-in-hand">
                    <unit-stack
                        v-for="(unit, index) of unitsInHand"
                        :key="`unit-in-hand-${index}`"
                        :units="[unit]"
                    />
                </div>
                <actions-log :actionsLog="actionsLog" v-if="position === 'top' && actionsLog && actionsLog.length"/>
            </div>
            <div class="mat-options mat-options-right">
                <div
                    class="units-group discarded-units"
                    :class="{[`unit-count-${groupUnitsByType(discardedUnits(player)).length}`]: true}"
                >
                    <unit-stack
                        v-for="(unitStack, index) of groupUnitsByType(discardedUnits(player))"
                        :key="`discarded-unit-stack-${index}`"
                        :units="unitStack"
                        :revealFacedown="showHand"
                        :actions="actionsState.actions"
                    />
                </div>
                <div class="bag-wrapper" :class="{['has-top-of-bag-units']: topOfBagUnits(player).length}">
                    <img class="bag" src="../../assets/bag.png" alt="coin-bag"/>
                    <span class="quantity-in-bag" v-if="showHand">{{ unitsInBag.length }}</span>
                    <div class="top-of-bag-units-wrapper" v-if="topOfBagUnits(player).length">
                        <unit-stack
                            :type="UNIT_STATE_TOP_OF_BAG"
                            :units="topOfBagUnits(player)"
                        />
                    </div>
                </div>
            </div>
        </div>
        <div class="player-label-wrapper" :class="{'left-align': decrees.length}">
            <player-label
                :player="player"
                :timeRemainingMs="isBlitzMode && (position === 'bottom' || !decrees.length) ? playerTimeRemainingMs : null"
                :theme="gameState.theme"
            />
        </div>
        <div class="time-remaining-label-wrapper" v-if="isBlitzMode && position === 'top' && decrees.length">
            <time-remaining-label :timeRemainingMs="playerTimeRemainingMs" :color="player.team.color"/>
        </div>
        <unit-types-modal
            :onCloseCallback="dismissUnitTypesModal"
            :show="showUnitTypesModal"
            :unitTypes="unitTypes"
        />
    </div>
</template>

<script>
import coinBackImg from '../../assets/coin_back.png';
import { API_BASE_URL } from '@/helpers/env';
import {
    DECREE_TYPE_ENLIST,
    DECREE_TYPE_REINFORCE,
} from '../DecreeCard/DecreeCard-types';
import gameState from '../Game/Game-state';
import {
    UNIT_STATE_IN_SUPPLY,
    UNIT_STATE_RECRUITED,
    UNIT_STATE_DISCARDED_FACEUP,
    UNIT_STATE_DISCARDED_FACEDOWN,
    UNIT_STATE_ELIMINATED,
    UNIT_TYPE_BISHOP,
    UNIT_STATE_TOP_OF_BAG,
} from '../Unit/Unit-types';

import {
    UNIT_GROUP_SUPPLY,
    UNIT_GROUP_HAND,
    UNIT_GROUP_DISCARD,
    UNIT_GROUP_ELIMINATED,
} from '../UnitGroup/UnitGroup-types';

export default {
    name: 'PlayerMat',
    props: [
        'actionsLog',
        'gameState',
        'player',
        'position',
        'showHand',
        'unitsInBag',
        'unitsInHand',
        'isBlitzMode',
    ],
    data() {
        return {
            discardedStates: [
                UNIT_STATE_RECRUITED,
                UNIT_STATE_DISCARDED_FACEDOWN,
                UNIT_STATE_DISCARDED_FACEUP,
            ],
            typeSupply: UNIT_GROUP_SUPPLY,
            typeHand: UNIT_GROUP_HAND,
            typeDiscard: UNIT_GROUP_DISCARD,
            typeEliminated: UNIT_GROUP_ELIMINATED,
            expandedGroup: UNIT_GROUP_SUPPLY,
            showUnitTypesModal: false,
            coinBackImg,
            ['UNIT_STATE_TOP_OF_BAG']: UNIT_STATE_TOP_OF_BAG,
        };
    },
    watch: {
        activeDecree(activeDecree, prevActiveDecree) {
            if (!prevActiveDecree && activeDecree === DECREE_TYPE_REINFORCE
                && this.expandedGroup !== UNIT_GROUP_ELIMINATED
                && this.position === 'bottom') {
                this.expandedGroup = UNIT_GROUP_ELIMINATED;
            }

            if (prevActiveDecree === DECREE_TYPE_REINFORCE && !activeDecree
                && this.expandedGroup !== UNIT_GROUP_SUPPLY
                && this.position === 'bottom') {
                this.expandedGroup = UNIT_GROUP_SUPPLY;
            }

            if (!prevActiveDecree && activeDecree === DECREE_TYPE_ENLIST
                && this.expandedGroup !== UNIT_GROUP_SUPPLY
                && this.position === 'bottom') {
                this.expandedGroup = UNIT_GROUP_SUPPLY;
            }
        },
    },
    computed: {
        activeDecree() {
            return this.$store.state.actionsState.activeDecree;
        },
        actionsState() {
            return this.$store.state.actionsState;
        },
        activePlayer() {
            return this.player.id === this.gameState.activePlayerId;
        },
        apiUrl() {
            return API_BASE_URL;
        },
        bishopRecruit() {
            return this.actionsState.bishopRecruit;
        },
        decrees() {
            return this.gameState.decrees;
        },
        facedownUnits() {
            return this.gameState.unitsInBag.facedownUnits;
        },
        imgBaseUrl() {
            return `${this.apiUrl}/assets`;
        },
        isVertical() {
            return ['left', 'right'].includes(this.position);
        },
        selectedBishopInHand() {
            return this.gameState.unitsInHand.filter(unit =>
                unit.type === UNIT_TYPE_BISHOP &&
                unit.id === this.actionsState.selectedUnitId,
            )[0];
        },
        playerTimeRemainingMs() {
            return this.$store.state.timeoutBlitzState.timeRemaining[this.player.id];
        },
        unitTypes() {
            return this.gameState.unitTypes.filter(unit => unit.playerId === this.player.id);
        },
    },
    methods: {
        discardedUnits(player) {
            return player.units.filter(unit => this.discardedStates.includes(unit.state));
        },
        dismissUnitTypesModal() {
            this.showUnitTypesModal = false;
        },
        eliminatedUnits(player) {
            return player.units.filter(unit => unit.state === UNIT_STATE_ELIMINATED);
        },
        topOfBagUnits(player) {
            return player.units.filter(unit => unit.state === UNIT_STATE_TOP_OF_BAG);
        },
        filterByType(units) {
            const types = [];
            const filteredUnits = [];
            let quantity = 0;

            for (const unit of units) {
                if (!types.includes(unit.type)) {
                    quantity = units.filter(currentUnit => currentUnit.type === unit.type).length;
                    types.push(unit.type);
                    filteredUnits.push({ ...unit, quantity });
                }
            }

            return filteredUnits;
        },
        groupUnitsByType(units = []) {
            const unitsByType = {};
            const groupedUnits = [];
			const sortedUnits = units.sort((unitA, unitB) => unitA.id - unitB.id);

            for (const unit of sortedUnits) {
                if (unitsByType[unit.type]) {
                    unitsByType[unit.type].push(unit);
                } else {
                    unitsByType[unit.type] = [unit];
                }
            }

            for (const [_, unitGroup] of Object.entries(unitsByType)) {
                groupedUnits.push(unitGroup);
            }

            return groupedUnits;
        },
        onToggleSupplyEliminated() {
            const {
                activeDecree,
                expandedGroup,
                typeEliminated,
                typeSupply,
            } = this;

            if (activeDecree === DECREE_TYPE_REINFORCE && expandedGroup === typeEliminated) {
                return;
            }

            if (activeDecree === DECREE_TYPE_ENLIST && expandedGroup === typeSupply) {
                return;
            }

            if (expandedGroup === typeSupply) {
                this.expandedGroup = typeEliminated;
            } else if (expandedGroup === typeEliminated) {
                this.expandedGroup = typeSupply;
            }
        },
        onViewCards() {
            this.showUnitTypesModal = true;
        },
        toggleRecruitType() {
            this.$store.dispatch('setBishopRecruit', { value: !this.bishopRecruit, notify: true });
        },
        unitsInSupply(player) {
            return player.units.filter(unit => unit.state === UNIT_STATE_IN_SUPPLY);
        },
    },
};
</script>

<style scoped lang="scss">
    @use "@/styles/styles.scss" as *;

    .mobile-player-mat {
        color: $sage-brown;
        display: flex;
        font-size: .9em;
        font-weight: bold;
        max-height: 100%;
        width: 100%;
        flex-direction: column;

        &.bottom {

            .player-label-wrapper {
                left: 50%;
                bottom: 0;
                transform: translateX(-50%);

                .player-label {
                    border-bottom-left-radius: 0;
                    border-bottom-right-radius: 0;

                    .text {
                        user-select: none;
                    }
                }
            }

            .mat-content {

                .mat-options {

                    &.mat-options-left,
                    &.mat-options-right {

                        .units-group {
                            padding-bottom: 20px;
                        }
                    }
                }
            }
        }

        &.top {

            .player-info-wrapper {
                order: 0;
            }

            .player-label-wrapper {
                left: 50%;
                top: 0;
                transform: translateX(-50%);

                &.left-align {
                    left: 3%;
                    transform: translateX(0);
                }

                .player-label {
                    border-top-left-radius: 0;
                    border-top-right-radius: 0;
                }
            }
            .time-remaining-label-wrapper {
                position: absolute;
                right: 3%;
                top: 0;

                .time-remaining-label {
                    border-top-left-radius: 0;
                    border-top-right-radius: 0;
                }
            }

            .bag-wrapper {
                bottom: -2px;
            }

            .mat-options-toolbar {
                bottom: -10px;
            }
        }

        .actions-log-wrapper {
            bottom: -1px;
            position: absolute;
            width: 100%;
        }

        .mat-content {
            display: flex;
            flex-grow: 1;

            .mat-options {
                position: relative;

                &.mat-options-left,
                &.mat-options-right {
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    min-width: 20%;
                    max-width: 20%;

                    .units-group  {
                        display: flex;
                        flex-wrap: wrap;
                        justify-content: center;

                        &.unit-count-5,
                        &.unit-count-6 {
                            .unit-stack {
                                width: 33%;
                            }
                        }

                        .unit-stack {
                            width: 50%;
                        }
                    }
                }

                .mat-option {
                    background: white;
                    box-sizing: border-box;
                    border: 1px solid $light-gray;
                    border-radius: 4px;
                    color: $sage-brown;
                    padding: 8px;
                    position: relative;

                    &:first-of-type {
                        margin-bottom: 5px;
                    }

                    &.active {
                        border-color: $sage-brown;
                    }
                }

                &.mat-options-center {
                    box-sizing: border-box;
                    display: flex;
                    flex: 1 1 auto;
                    flex-direction: column;
                    justify-content: center;
                    padding: 0 7.5px;
                    position: relative;
                    min-width: 60%;
                    max-width: 60%;

                    .units-group {
                        align-self: center;
                        display: flex;
                        flex-grow: 1;
                        justify-content: center;
                        padding: 0 7.5px;
                        max-height: 80%;
                        width: 100%;

                        .unit-stack {
                            align-self: center;
                            margin: 0 1%;
                            width: 25%;
                            $zIndex: 10;

                            @for $i from 1 through 10 {
                                &:nth-of-type(#{$i}) {
                                    z-index: #{$zIndex - $i};
                                }
                            }
                        }
                    }
                }
            }
        }

        .player-label-wrapper {
            display: flex;
            position: absolute;

            .player-label {
                border-width: 0;
                box-shadow: 0px 1px 3px 1px rgba(123,125,125,0.25);
                font-size: .7em;
                padding: 3px 4px;

                .royal-coin-wrapper {

                    .royal-coin {
                        height: 11px;
                        width: 11px;
                    }
                }
            }
        }

        .units-section {
            width: 33.33%;

            &.section-middle {
                flex: 1 1 auto;
            }

            &.section-left {

                .units {
                    flex-wrap: wrap;

                    .unit {
                        width: 50%;
                    }
                }
            }

            .units-section-title {
                font-family: 'Livingstone';

                .units-section-title-text {
                    border-bottom: 1px solid $burnished-gold;

                    &.title-eliminated {
                        margin-left: 10px;
                    }
                }
            }
        }

        .bag-wrapper {
            background: white;
            border-radius: 4px;
            border: 1px solid $sage-brown;
            bottom: 9px;
            display: flex;
            justify-content: space-evenly;
            left: 50%;
            margin: 0 auto;
            max-height: 15px;
            padding: 2px 4px;
            position: absolute;
            transform: translateX(-50%);

            > * {
                align-self: center;
            }

            .bag {
                max-height: 15px;
                width: auto;
            }

            .quantity-in-bag {
                margin-left: 3px;
                font-size: 1em;
            }
        }

        .mat-options-toolbar {
            position: absolute;
            bottom: 0;
            height: 38px;
            width: 100%;
            z-index: 1;

            .switch-toggle {
                position: absolute;
                top: 50%;
                transform: translateY(-50%);
                right: -9px;
            }

            .view-cards {
                background: white;
                border: 1px solid $sage-brown;
                cursor: pointer;
                display: inline-block;
                left: 0;
                position: absolute;
                border-radius: 50%;
                height: 15px;
                padding: 2px;
                top: 50%;
                transform: translateY(-50%);

                .view-cards-icon {
                    fill: $sage-brown;
                    height: 15px;
                    width: auto;
                }
            }
        }

        .top-of-bag-units-wrapper {
            position: absolute;
            width: 70%;
            top: 58%;
            right: 110%;
            transform: translateY(-50%);
        }
    }

    @media only screen and (min-width: $screen-xxs) {

        .mobile-player-mat {

            &.bottom {

                .mat-content {

                    .mat-options {

                        &.mat-options-left,
                        &.mat-options-right {

                            .units-group {
                                padding-bottom: 0;
                            }
                        }
                    }
                }
            }

            &.top {

                .bag-wrapper {
                    bottom: 9px;
                }

                .mat-options-toolbar {
                    bottom: 0;
                }
            }

            .actions-log-wrapper {
                bottom: 9px;
            }

            .mat-options-toolbar {

                .view-cards {
                    left: 4px;
                }
            }
        }
    }

    @media only screen and (min-width: $screen-md) {

    }

</style>
